// src/SelectedSpace/RoomSideBar.tsx
import React, { useMemo } from "react";
import { ActionIcon, Box, Flex, Text, Tooltip, Transition } from "@mantine/core";
import { MdClose } from "react-icons/md";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setSelectedRoomTabInRedux, setSelectedSpaceNumberInRedux } from "../DesignTab/designSlice";
import StandardRoom from "./StandardRoom/StandardRoom";
import { ISpaceValidation } from "./SpaceValidations/spaceValidationsUtils";
import RoomCeilingValidations from "./SpaceValidations/RoomCeilingValidations";
import { InvalidIcon } from "../PingToPyCeiling";
import {
  useGetMcbimItemsInSpaceQuery,
  useGetMcbimSingleMcbimSpaceQuery,
  useGetMultiplacerSingleRoomQuery,
} from "../../openapi";
import { selectHttpDevicesInRoom } from "../DesignTab/devices/deviceSlice";
import {
  selectActiveSpaceHasNoWarnings,
  selectRoomSideBarTabs,
  selectSpaceValidationForSelectedSpace,
} from "../DesignTab/designSelector";
import { skipToken } from "@reduxjs/toolkit/query";
import { RoomInfoViewer } from "./RoomInfoViewer";
import { DrofusStandardRoomDiffAllDisciplines } from "./StandardRoomValidation/DrofusStandardRoomDiffAllDisciplines";
import { RoomSideBarTabs } from "@multiconsult-group/multiplacer-core";
import { ROOM_SIDEBAR_TAB_CONFIG } from "./roomSidebarTabConfig";
import MultiplacerCeilingDevices from "./MultiplacerCeilingDevices/MultiplacerCeilingDevices";
import { selectHttpCurrentProjct } from "../projectSelectors";
import { selectMcBimItemsInSelectedRoomArgs } from "./singleRoomSelectors";
import { DrofusSpecifiedArticlesDiffAllResponsibilities } from "./DrofusSpecifiedArticlesDiffAllResponsibilities";
import { selectGetMcbimStandardSpaceArgs } from "../McBim/httpArgsMcBimSelectors";
import { RoomSectionsPanel } from "../RoomSectionViews/RoomSectionsPanel";
import { RoomNumberingDetailsPanel } from "./RoomNumberingDetailsPanel";
import { CeilingCommentsPanel } from "./CeilingComments/CeilingCommentsPanel";
import { selectHttpSingleRoomFull } from "../Room/httpRoomSelectors";

export const RoomSideBar: React.FC = () => {
  const dispatch = useAppDispatch();

  const project = useAppSelector(selectHttpCurrentProjct);
  const selectedRoomTab = useAppSelector((state) => state.design.selectedRoomTab) as
    | RoomSideBarTabs
    | undefined;
  const selectedSpaceNumber = useAppSelector((state) => state.design.selectedSpaceNumber) as string;

  const { data: singleRoom } = useGetMultiplacerSingleRoomQuery(
    project && selectedSpaceNumber
      ? { projectNo: project.projectNo, roomNumber: selectedSpaceNumber }
      : skipToken
  );

  const devicesInRoom = useAppSelector(selectHttpDevicesInRoom);

  const mcbimStandardSpaceArgs = useAppSelector(selectGetMcbimStandardSpaceArgs);

  const { data: mcbimSpaceStandardRoom } = useGetMcbimSingleMcbimSpaceQuery(
    mcbimStandardSpaceArgs ?? skipToken
  );

  const { data: mcbimItemsInStandardSpace = [] } = useGetMcbimItemsInSpaceQuery(
    {
      projectNo: project.projectNo,
      spaceId: mcbimSpaceStandardRoom?._id as string,
    },
    { skip: mcbimSpaceStandardRoom == null }
  );

  useGetMcbimItemsInSpaceQuery(useAppSelector(selectMcBimItemsInSelectedRoomArgs) ?? skipToken);

  const spaceValidations = useAppSelector(selectSpaceValidationForSelectedSpace);

  return (
    <Box
      c="black"
      bg="#e1e1e1b8"
      h="100%"
      w="100%"
      style={{ display: "flex", flexDirection: "column", backdropFilter: "blur(8px)" }}
    >
      <Transition mounted transition="slide-down" duration={200} timingFunction="ease-out">
        {(styles) => (
          <Flex align="center" justify="space-between" px="md" py="xs" bg="blue.0" style={styles}>
            <Text c="black" size="sm" fw={600} lineClamp={1}>
              {singleRoom?.roomNumber}, {singleRoom?.roomName}
            </Text>

            <ActionIcon
              size="sm"
              radius="xl"
              variant="subtle"
              aria-label="Lukk rompanelet"
              onClick={() => dispatch(setSelectedSpaceNumberInRedux(undefined))}
            >
              <MdClose size={16} />
            </ActionIcon>
          </Flex>
        )}
      </Transition>

      <Box
        w="100%"
        style={{ display: "flex", flex: 1, minHeight: 0, justifyContent: "space-between" }}
      >
        <Box h="100%" w="100%" style={{ minWidth: 0, overflowY: "auto", overflowX: "hidden" }}>
          {selectedRoomTab === RoomSideBarTabs.CEILING_MULTIPLACER_PRODUCTS && (
            <MultiplacerCeilingDevices />
          )}

          {selectedRoomTab === RoomSideBarTabs.ROOM_INFO && singleRoom != null && (
            <RoomInfoViewer />
          )}

          {selectedRoomTab === RoomSideBarTabs.ROOM_SECTION_VIEWS && <RoomSectionsPanel />}

          {selectedRoomTab === RoomSideBarTabs.CEILING_STANDARD_ROOM &&
            singleRoom?.rfpMalRom != null &&
            mcbimSpaceStandardRoom != null && (
              <StandardRoom />
            )}

          {selectedRoomTab === RoomSideBarTabs.CEILING_VALIDATIONS && (
            <RoomCeilingValidations
              spaceValidations={spaceValidations}
              mcbimItemsInStandardSpace={mcbimItemsInStandardSpace}
              devices={devicesInRoom}
            />
          )}
          {selectedRoomTab === RoomSideBarTabs.CEILING_COMMENTS && <CeilingCommentsPanel />}
          {selectedRoomTab === RoomSideBarTabs.RFP_STANDARD_ROOM_DIFF && (
            <DrofusStandardRoomDiffAllDisciplines />
          )}
          {selectedRoomTab === RoomSideBarTabs.ROOM_SPECIFIED_ARTICLES && (
            <DrofusSpecifiedArticlesDiffAllResponsibilities />
          )}
          {selectedRoomTab === RoomSideBarTabs.ROOM_NUMBERING_DETAILS && (
            <RoomNumberingDetailsPanel />
          )}
        </Box>
        <IconVerticalBar spaceValidations={spaceValidations} />
      </Box>
    </Box>
  );
};

const IconVerticalBar: React.FC<{ spaceValidations: ISpaceValidation[] }> = () => {
  const dispatch = useAppDispatch();

  const selectedRoomTab = useAppSelector((state) => state.design.selectedRoomTab) as
    | RoomSideBarTabs
    | undefined;

  const spaceIsValid = useAppSelector(selectActiveSpaceHasNoWarnings);
  const iconKeys = useAppSelector(selectRoomSideBarTabs) as RoomSideBarTabs[];
  const singleRoom = useAppSelector(selectHttpSingleRoomFull);
  const ceilingCommentCount = singleRoom?.ceilingComments?.length ?? 0;

  const icons = useMemo(
    () =>
      iconKeys.map((tabKey) => {
        const base = ROOM_SIDEBAR_TAB_CONFIG[tabKey];
        if (!base) return null;

        let icon = base.icon;
        let color: string | undefined;
        const tooltip = base.tooltip;

        // Spesialhåndtering for varslings-ikon (grønn/rød)
        if (tabKey === RoomSideBarTabs.CEILING_VALIDATIONS) {
          icon = !spaceIsValid ? InvalidIcon : base.icon;
          color = !spaceIsValid ? "red" : "#40c057";
        }

        return {
          key: tabKey,
          label: base.label,
          icon,
          tooltip,
          color,
        };
      }),
    [iconKeys, spaceIsValid]
  );

  return (
    <Box h="100%" bg="gray.2">
      <ActionIcon.Group orientation="vertical">
        {icons.map((iconItem) => {
          if (iconItem == null) return null;
          const { key, label, icon: IconComp, tooltip, color } = iconItem;

          return (
            <Tooltip label={tooltip} key={key} position="left">
              <ActionIcon
                style={{ outline: "none", position: "relative" }}
                variant="default"
                size="md"
                aria-label={label}
                color={selectedRoomTab === key ? "blue" : "gray"}
                onClick={() => dispatch(setSelectedRoomTabInRedux(key))}
              >
                {color == null ? <IconComp /> : <IconComp color={color} />}
                {key === RoomSideBarTabs.CEILING_COMMENTS && ceilingCommentCount > 0 && (
                  <Box
                    pos="absolute"
                    top={0}
                    right={0}
                    bg="orange.5"
                    c="white"
                    px={3}
                    style={{
                      minWidth: 14,
                      height: 14,
                      lineHeight: "14px",
                      fontSize: 10,
                      fontWeight: 700,
                      textAlign: "center",
                      borderRadius: 0,
                      pointerEvents: "none",
                    }}
                  >
                    {ceilingCommentCount > 9 ? "9+" : ceilingCommentCount}
                  </Box>
                )}
              </ActionIcon>
            </Tooltip>
          );
        })}
      </ActionIcon.Group>
    </Box>
  );
};
